import React from 'react';
import { NAVY, TEAL, SLATE, TEAL_DARK, MAUVE, LIGHT_BLUE, pillarColour } from './palette';

// In-grid graphics: inline SVG so the results grid draws without a chart library per row.

// 0-100 percentile as a bar on a light track; thin cohorts carry a marker.
export function PercentileBar({ pct, thin, n, width = 64 }) {
  if (pct === null || pct === undefined) return <span className="qs-dash">–</span>;
  const w = Math.max(0, Math.min(100, pct)) / 100 * width;
  return (
    <span className="qs-pbar" title={`${Math.round(pct)}th percentile of ${n} funds${thin ? ' (thin cohort)' : ''}`}>
      <svg width={width} height={10} aria-hidden="true">
        <rect x={0} y={1} width={width} height={8} rx={2} fill={LIGHT_BLUE} opacity={0.55} />
        <rect x={0} y={1} width={w} height={8} rx={2} fill={thin ? SLATE : NAVY} />
      </svg>
      <span className="qs-pbar-num">{Math.round(pct)}</span>
      {thin && <span className="qs-thin" title={`Only ${n} funds have a value in this cohort`}>thin</span>}
    </span>
  );
}

// Stacked bar of pillar contributions (each already scaled to its share of the total).
export function ScoreBar({ score, pillarNames, width = 90 }) {
  if (!score || score.total === null || score.total === undefined) return <span className="qs-dash">–</span>;
  const widths = pillarNames.map((p) => Math.max(0, score.pillars?.[p]?.contribution || 0) / 100 * width);
  const starts = widths.map((_, i) => widths.slice(0, i).reduce((a, b) => a + b, 0));
  const title = pillarNames.map((p) => {
    const s = score.pillars?.[p];
    return `${p}: ${s?.score === null || s?.score === undefined ? '–' : s.score.toFixed(1)} (contributes ${(s?.contribution ?? 0).toFixed(1)})`;
  }).join('\n') + `\nmetrics used ${score.metrics_used}/${score.metrics_total}`;
  return (
    <span className="qs-sbar" title={title}>
      <svg width={width} height={12} aria-hidden="true">
        <rect x={0} y={1} width={width} height={10} rx={2} fill={LIGHT_BLUE} opacity={0.4} />
        {pillarNames.map((p, i) => (
          <rect key={p} x={starts[i]} y={1} width={widths[i]} height={10} fill={pillarColour(i)} />
        ))}
      </svg>
      <span className="qs-sbar-num">{score.total.toFixed(1)}</span>
      {score.metrics_used < score.metrics_total && (
        <span className="qs-partial" title={`Scored on ${score.metrics_used} of ${score.metrics_total} metrics`}>
          {score.metrics_used}/{score.metrics_total}
        </span>
      )}
    </span>
  );
}

// Rolling series with a zero line and an emphasised endpoint; nulls break the line.
export function Sparkline({ values, width = 96, height = 26 }) {
  if (!values || !values.some((v) => v !== null && v !== undefined)) return <span className="qs-dash">–</span>;
  const nums = values.filter((v) => v !== null && v !== undefined);
  let lo = Math.min(0, ...nums);
  let hi = Math.max(0, ...nums);
  if (hi === lo) { hi += 1e-6; lo -= 1e-6; }
  const pad = 2;
  const sx = (i) => pad + (i / Math.max(1, values.length - 1)) * (width - 2 * pad);
  const sy = (v) => pad + (1 - (v - lo) / (hi - lo)) * (height - 2 * pad);
  const segments = [];
  values.forEach((v, i) => {
    if (v === null || v === undefined) { segments.push([]); return; }
    if (!segments.length) segments.push([]);
    segments[segments.length - 1].push(`${sx(i).toFixed(1)},${sy(v).toFixed(1)}`);
  });
  const lastIdx = values.reduce((acc, v, i) => (v === null || v === undefined ? acc : i), -1);
  const last = values[lastIdx];
  return (
    <svg className="qs-spark" width={width} height={height} aria-hidden="true">
      <line x1={pad} x2={width - pad} y1={sy(0)} y2={sy(0)} stroke={LIGHT_BLUE} strokeWidth={1} />
      {segments.filter((s) => s.length).map((pts, k) => (
        <polyline key={k} points={pts.join(' ')} fill="none" stroke={TEAL} strokeWidth={1.4} strokeLinejoin="round" />
      ))}
      {lastIdx >= 0 && <circle cx={sx(lastIdx)} cy={sy(last)} r={2.4} fill={last >= 0 ? TEAL_DARK : MAUVE} />}
    </svg>
  );
}
